import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import RegenerateInvoiceModal from "components/common/RegenerateInvoiceModal";
import CancelInvoiceModal from "components/common/CancelInvoiceModal";
import {
  approvePettyWorkForBilling,
  cancelPettyWork,
  holdPettyWork,
  regeneratePettyWorkInvoice,
  resumePettyWork,
  startPettyWork,
  submitPettyWorkBilling,
} from "features/PettyWorks/services/PettyWorkService";
import { cancelInvoice } from "features/Invoices/services/InvoiceService";
import type {
  PettyWork,
  PettyWorkBoqLinePayload,
  PettyWorkItem,
} from "features/PettyWorks/types/petty-work.types";
import { pettyWorkLineQuantity } from "features/PettyWorks/utils/petty-work.utils";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import CancelModal from "../CancelModal";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";

export type PettyWorkLifecycleIntent =
  | { kind: "start" }
  | { kind: "hold" }
  | { kind: "resume" }
  | { kind: "approveForBilling" }
  | { kind: "submitBilling" }
  | { kind: "cancel" }
  | { kind: "regenerateInvoice" }
  | { kind: "cancelActiveInvoice"; invoiceId: number };

type ConfirmKind = "start" | "resume" | "approveForBilling";

const confirmCopy: Record<
  ConfirmKind,
  {
    title: string;
    iconClass: string;
    iconColor: string;
    heading: string;
    description: string;
    confirm: string;
    confirmColor: string;
  }
> = {
  start: {
    title: "Start petty work",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Start work on this petty work?",
    description: "Status will move to In progress.",
    confirm: "Start work",
    confirmColor: "success",
  },
  resume: {
    title: "Resume work",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Resume this petty work?",
    description: "Work will move back to In progress.",
    confirm: "Resume",
    confirmColor: "success",
  },
  approveForBilling: {
    title: "Approve for billing",
    iconClass: "ri-check-double-line",
    iconColor: "text-success",
    heading: "Approve this petty work for billing?",
    description:
      "Status will move to Ready for billing. Accounts can then submit billing to generate the invoice.",
    confirm: "Approve for billing",
    confirmColor: "success",
  },
};

/** BOQ form row for the Regenerate Invoice items editor (matches the create/update form's shape). */
type PettyWorkBoqFormRow = {
  type: QuotationItemType;
  description: string;
  lineId?: number;
  quantity: number;
  unit: string;
  rate: number;
};

function pettyWorkItemToBoqRow(item: PettyWorkItem): PettyWorkBoqFormRow {
  return {
    type: (item.type as QuotationItemType) ?? QuotationItemType.MATERIAL,
    description: item.description ?? "",
    lineId:
      item.id != null && Number.isFinite(Number(item.id)) && Number(item.id) > 0
        ? Number(item.id)
        : undefined,
    quantity: pettyWorkLineQuantity(item),
    unit: item.unit ?? "",
    rate: item.rate ?? 0,
  };
}

function emptyPettyWorkBoqRow(): PettyWorkBoqFormRow {
  return {
    type: QuotationItemType.MATERIAL,
    description: "",
    lineId: undefined,
    quantity: 1,
    unit: "",
    rate: 0,
  };
}

function pettyWorkBoqRowToApiLine(
  row: PettyWorkBoqFormRow,
): PettyWorkBoqLinePayload {
  return {
    quantity: Number(row.quantity),
    type: row.type as PettyWorkBoqLinePayload["type"],
    description: String(row.description ?? "").trim(),
    unit: String(row.unit ?? "").trim(),
    rate: Number(row.rate),
  };
}

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type Props = {
  pettyWork: PettyWork | null;
  intent: PettyWorkLifecycleIntent | null;
  onFinished: () => void;
  onSuccess: () => void | Promise<void>;
};

export default function PettyWorkLifecycleModals({
  pettyWork,
  intent,
  onFinished,
  onSuccess,
}: Props) {
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  // Hold
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  // Cancel
  const [cancelOpen, setCancelOpen] = useState(false);

  // Submit billing
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingAmount, setBillingAmount] = useState<string>("");
  const [billingSubmitting, setBillingSubmitting] = useState(false);
  const [draftAdvanceCount, setDraftAdvanceCount] = useState(0);

  // Regenerate invoice
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  // Cancel active invoice
  const [cancelInvoiceOpen, setCancelInvoiceOpen] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState<number | null>(null);

  const pettyWorkItems = pettyWork?.items;
  const itemsEditor = useMemo(() => {
    if (pettyWorkItems == null) return undefined;
    const initialRows: PettyWorkBoqFormRow[] = pettyWorkItems.length
      ? pettyWorkItems.map((item) => pettyWorkItemToBoqRow(item))
      : [emptyPettyWorkBoqRow()];
    return {
      initialRows,
      getDefaultRow: emptyPettyWorkBoqRow,
      toPayloadItems: (rows: PettyWorkBoqFormRow[]) =>
        rows.map((row) => pettyWorkBoqRowToApiLine(row)),
    };
  }, [pettyWorkItems]);

  const runSuccess = useCallback(async () => {
    await onSuccess();
  }, [onSuccess]);

  const closeAll = useCallback(() => {
    setConfirmKind(null);
    setHoldOpen(false);
    setCancelOpen(false);
    setBillingOpen(false);
    setRegenerateOpen(false);
    setCancelInvoiceOpen(false);
  }, []);

  useEffect(() => {
    if (!pettyWork || !intent) {
      closeAll();
      return;
    }
    closeAll();
    setHoldReason("");
    setBillingAmount("");
    setDraftAdvanceCount(0);
    switch (intent.kind) {
      case "start":
        setConfirmKind("start");
        break;
      case "hold":
        setHoldOpen(true);
        break;
      case "resume":
        setConfirmKind("resume");
        break;
      case "cancel":
        setCancelOpen(true);
        break;
      case "approveForBilling":
        setConfirmKind("approveForBilling");
        break;
      case "submitBilling":
        setBillingOpen(true);
        break;
      case "regenerateInvoice":
        setRegenerateOpen(true);
        break;
      case "cancelActiveInvoice":
        setCancelInvoiceId(intent.invoiceId);
        setCancelInvoiceOpen(true);
        break;
      default:
        break;
    }
  }, [pettyWork, intent, closeAll]);

  // --- Simple confirmations (start, resume, approveForBilling) ---
  const handleConfirmLifecycle = useCallback(async () => {
    if (!pettyWork || !confirmKind) return;
    setConfirmSubmitting(true);
    try {
      let res;
      if (confirmKind === "start") res = await startPettyWork(pettyWork.id);
      else if (confirmKind === "resume") res = await resumePettyWork(pettyWork.id);
      else res = await approvePettyWorkForBilling(pettyWork.id);

      if (res.success) {
        toast.success(res.message ?? "Updated.");
        setConfirmKind(null);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Request failed.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Request failed."));
    } finally {
      setConfirmSubmitting(false);
    }
  }, [confirmKind, onFinished, runSuccess, pettyWork]);

  // --- Hold ---
  const handleHold = useCallback(async () => {
    if (!pettyWork || !holdReason.trim()) return;
    setHoldSubmitting(true);
    try {
      const res = await holdPettyWork(pettyWork.id, {
        reason: holdReason.trim(),
      });
      if (res.success) {
        toast.success(res.message ?? "Petty work put on hold.");
        setHoldOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not put petty work on hold.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not put petty work on hold."));
    } finally {
      setHoldSubmitting(false);
    }
  }, [holdReason, onFinished, runSuccess, pettyWork]);

  // --- Cancel ---
  const handleCancel = useCallback(
    async (reason: string | null) => {
      if (!pettyWork) return;
      try {
        const res = await cancelPettyWork(pettyWork.id, { reason });
        if (res.success) {
          toast.success(res.message ?? "Petty work cancelled.");
          setCancelOpen(false);
          await runSuccess();
          onFinished();
        } else {
          toast.error(res.message ?? "Could not cancel petty work.");
        }
      } catch (err: unknown) {
        toast.error(axiosMessage(err, "Could not cancel petty work."));
      }
    },
    [onFinished, runSuccess, pettyWork],
  );

  // --- Submit billing ---
  const handleSubmitBilling = useCallback(async () => {
    if (!pettyWork) return;
    const parsed = billingAmount.trim() !== "" ? Number(billingAmount) : null;
    if (billingAmount.trim() !== "" && (isNaN(parsed!) || parsed! < 0)) {
      toast.error("Please enter a valid billed amount.");
      return;
    }
    setBillingSubmitting(true);
    try {
      const res = await submitPettyWorkBilling(pettyWork.id, {
        finalAmount: parsed,
      });
      if (res.success) {
        toast.success(res.message ?? "Billing submitted. Invoice generated.");
        setBillingOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not submit billing.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not submit billing."));
    } finally {
      setBillingSubmitting(false);
    }
  }, [billingAmount, onFinished, runSuccess, pettyWork]);

  const confirmCopyEntry = confirmKind ? confirmCopy[confirmKind] : null;

  return (
    <>
      {/* Simple confirmations */}
      {confirmCopyEntry ? (
        <ConfirmActionModal
          show={Boolean(confirmKind)}
          onClose={() => {
            if (!confirmSubmitting) {
              setConfirmKind(null);
              onFinished();
            }
          }}
          title={confirmCopyEntry.title}
          iconClass={confirmCopyEntry.iconClass}
          iconColor={confirmCopyEntry.iconColor}
          heading={confirmCopyEntry.heading}
          description={confirmCopyEntry.description}
          confirmText={confirmSubmitting ? "Please wait…" : confirmCopyEntry.confirm}
          confirmColor={confirmCopyEntry.confirmColor}
          confirmDisabled={confirmSubmitting}
          onConfirm={() => void handleConfirmLifecycle()}
        />
      ) : null}

      {/* Hold — collects required reason */}
      <Modal
        isOpen={holdOpen}
        toggle={() => {
          if (!holdSubmitting) {
            setHoldOpen(false);
            onFinished();
          }
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            if (!holdSubmitting) {
              setHoldOpen(false);
              onFinished();
            }
          }}
        >
          Put work on hold
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Work will move to <strong>On hold</strong> until someone resumes it.
          </p>
          <Label className="form-label">
            Reason <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Explain why the work is being paused…"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            disabled={holdSubmitting}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={holdSubmitting}
            onClick={() => {
              setHoldOpen(false);
              onFinished();
            }}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={holdSubmitting || !holdReason.trim()}
            onClick={() => void handleHold()}
          >
            {holdSubmitting ? "Please wait…" : "Put on hold"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Submit billing — optional finalAmount */}
      <Modal
        isOpen={billingOpen}
        toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}>
          Submit billing
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This will generate the invoice from the line items, settle any linked client advance,
            and close work assignments. Status will move to <strong>Completed</strong>.
          </p>
          {pettyWork ? (
            <p className="text-muted small mb-3">
              Current agreed amount:{" "}
              <span className="text-body fw-medium">
                {pettyWork.agreedAmount != null
                  ? formatCurrency(pettyWork.agreedAmount)
                  : "—"}
              </span>
            </p>
          ) : null}
          <Label className="form-label">
            Final billed amount <span className="text-muted fw-normal">(optional — leave blank to use agreed amount)</span>
          </Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 15000"
            value={billingAmount}
            onChange={(e) => setBillingAmount(e.target.value)}
            disabled={billingSubmitting}
          />
          {draftAdvanceCount > 0 ? (
            <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
              Cannot proceed: {draftAdvanceCount} advance
              {draftAdvanceCount !== 1 ? "s" : ""} linked to this record{" "}
              {draftAdvanceCount !== 1 ? "are" : "is"} still awaiting receipt.
              All advances must be received or cancelled first.
            </div>
          ) : null}
          {pettyWork ? (
            <LinkedClientAdvancesTable
              className="mt-3"
              sourceType={ClientAdvanceSourceType.PETTY_WORK}
              sourceId={pettyWork.id}
              onLoaded={(advances: ClientAdvance[]) =>
                setDraftAdvanceCount(
                  advances.filter((a) => a.status === ClientAdvanceStatus.DRAFT)
                    .length,
                )
              }
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={billingSubmitting} onClick={() => { setBillingOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={billingSubmitting || draftAdvanceCount > 0}
            onClick={() => void handleSubmitBilling()}
          >
            {billingSubmitting ? "Submitting…" : "Submit billing"}
          </Button>
        </ModalFooter>
      </Modal>

      <CancelModal
        show={cancelOpen}
        onCancelClick={(reason) => void handleCancel(reason)}
        onCloseClick={() => {
          setCancelOpen(false);
          onFinished();
        }}
        record={pettyWork}
      />

      {/* Regenerate invoice — only runs when no active invoice exists */}
      <RegenerateInvoiceModal
        show={regenerateOpen}
        onClose={() => {
          setRegenerateOpen(false);
          onFinished();
        }}
        onSuccess={runSuccess}
        currentFinalAmount={pettyWork?.finalAmount}
        itemsEditor={itemsEditor}
        submit={(payload) => {
          if (!pettyWork) throw new Error("No petty work loaded.");
          return regeneratePettyWorkInvoice(pettyWork.id, payload);
        }}
      />

      {/* Cancel active invoice — the only step that ever cancels one */}
      <CancelInvoiceModal
        show={cancelInvoiceOpen}
        onClose={() => {
          setCancelInvoiceOpen(false);
          onFinished();
        }}
        onSuccess={runSuccess}
        submit={(payload) => {
          if (!cancelInvoiceId) throw new Error("No invoice selected.");
          return cancelInvoice(cancelInvoiceId, payload);
        }}
      />
    </>
  );
}
